<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import useSysStore from '@/store/modules/sys'
import { useSignInNotifyModal } from '@/hooks/useSignInNotifyModal'

const { t } = useI18n()
const router = useRouter()
const sysStore = useSysStore()
const {
  signInNotifyState,
  closeSignInNotifyModal,
  goToNextSignInNotifyTask,
  goToPrevSignInNotifyTask,
  setCurrentSignInNotifyTask,
  getSignInNotifyTaskList
} = useSignInNotifyModal()

const taskList = computed(() => getSignInNotifyTaskList())
const currentTask = computed(() => taskList.value[signInNotifyState.currentTaskIndex])
const hasPrevTask = computed(() => signInNotifyState.currentTaskIndex > 0)
const hasNextTask = computed(() => signInNotifyState.currentTaskIndex < taskList.value.length - 1)
const hasCountdown = computed(() => !!currentTask.value?.roundEndTime)
const mobileTrackStyle = computed(() => ({
  '--active-index': signInNotifyState.currentTaskIndex,
  '--drag-offset': `${dragOffsetX.value}px`
}))

const countdown = ref({ h: '--', m: '--', s: '--' })
let animationFrameId: number | null = null
const swipeStartX = ref(0)
const swipeStartY = ref(0)
const dragOffsetX = ref(0)
const isDragging = ref(false)
const isHorizontalSwipe = ref(false)

const weekdayMap = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
const signInNotifyModalZIndex = 2100

const getWeekTextByTask = (task?: (typeof taskList.value)[number]) => {
  const courseWeek = task?.courseWeek
  if (!courseWeek) {
    return ''
  }
  return t('task.signIn.weekOnly', { week: courseWeek })
}

const getWeekdayTextByTask = (task?: (typeof taskList.value)[number]) => {
  const dayOfWeek = task?.dayOfWeek
  if (!dayOfWeek || dayOfWeek < 1 || dayOfWeek > 7) {
    return ''
  }
  return t(`task.signIn.weekdays.${weekdayMap[dayOfWeek - 1]}`)
}

const getTitleByTask = (task?: (typeof taskList.value)[number]) => {
  if (!task) {
    return ''
  }
  const courseName = task.courseName || t('task.signIn.defaultCourseName')
  const taskName = task.taskName || t('task.signIn.defaultTaskName')
  const taskRound = task.roundNum || 1
  if (taskRound > 1) {
    return t('task.signIn.nthCheckInStarted', {
      round: taskRound,
      courseName,
      taskName
    })
  }
  return t('task.signIn.courseCheckInStarted', {
    courseName,
    taskName
  })
}

const getWeekText = computed(() => getWeekTextByTask(currentTask.value))
const getWeekdayText = computed(() => getWeekdayTextByTask(currentTask.value))
const getTitle = computed(() => getTitleByTask(currentTask.value))

const isTaskCountdownEnded = (task?: (typeof taskList.value)[number]) => {
  if (!task?.roundEndTime) {
    return false
  }
  return dayjs(task.roundEndTime).valueOf() - Date.now() <= 0
}

const getStatusTextByTask = (task?: (typeof taskList.value)[number]) => {
  return isTaskCountdownEnded(task)
    ? t('task.signIn.checkInEnded')
    : t('task.signIn.checkInProgress')
}

const startCountdown = () => {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
  const update = () => {
    const roundEndTime = currentTask.value?.roundEndTime
    if (!roundEndTime) {
      countdown.value = { h: '--', m: '--', s: '--' }
      return
    }
    const diff = dayjs(roundEndTime).valueOf() - Date.now()
    if (diff <= 0) {
      countdown.value = { h: '00', m: '00', s: '00' }
      return
    }
    const h = Math.floor(diff / 3600000)
    const m = Math.floor((diff % 3600000) / 60000)
    const s = Math.floor((diff % 60000) / 1000)
    countdown.value = {
      h: h.toString().padStart(2, '0'),
      m: m.toString().padStart(2, '0'),
      s: s.toString().padStart(2, '0')
    }
    animationFrameId = requestAnimationFrame(update)
  }
  update()
}

watch(
  () => signInNotifyState.visible,
  val => {
    if (val) {
      startCountdown()
      return
    }
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }
  }
)

watch(
  () => signInNotifyState.currentTaskIndex,
  () => {
    if (signInNotifyState.visible) {
      startCountdown()
    }
  }
)

onUnmounted(() => {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
})

const handleTouchStart = (event: TouchEvent) => {
  swipeStartX.value = event.changedTouches[0]?.clientX || 0
  swipeStartY.value = event.changedTouches[0]?.clientY || 0
  dragOffsetX.value = 0
  isDragging.value = true
  isHorizontalSwipe.value = false
}

const handleTouchMove = (event: TouchEvent) => {
  if (!isDragging.value) {
    return
  }
  const currentX = event.changedTouches[0]?.clientX || 0
  const currentY = event.changedTouches[0]?.clientY || 0
  const deltaX = currentX - swipeStartX.value
  const deltaY = currentY - swipeStartY.value
  if (!isHorizontalSwipe.value) {
    const isMoveEnough = Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6
    if (!isMoveEnough) {
      return
    }
    isHorizontalSwipe.value = Math.abs(deltaX) > Math.abs(deltaY)
  }
  if (!isHorizontalSwipe.value) {
    return
  }
  event.preventDefault()
  const atFirst = signInNotifyState.currentTaskIndex === 0
  const atLast = signInNotifyState.currentTaskIndex === taskList.value.length - 1
  if ((atFirst && deltaX > 0) || (atLast && deltaX < 0)) {
    dragOffsetX.value = deltaX * 0.35
    return
  }
  dragOffsetX.value = deltaX
}

const handleTouchEnd = () => {
  if (!isDragging.value) {
    return
  }
  const deltaX = dragOffsetX.value
  const threshold = 56
  if (isHorizontalSwipe.value && Math.abs(deltaX) >= threshold) {
    if (deltaX < 0 && hasNextTask.value) {
      goToNextSignInNotifyTask()
    } else if (deltaX > 0 && hasPrevTask.value) {
      goToPrevSignInNotifyTask()
    }
  }
  dragOffsetX.value = 0
  isDragging.value = false
  isHorizontalSwipe.value = false
}

const handleGoSignIn = () => {
  const task = currentTask.value
  if (!task || isTaskCountdownEnded(task)) {
    return
  }
  router.push({
    path: '/task/sign-in',
    query: {
      taskId: String(task.taskId),
      classId: String(task.classId),
      courseId: String(task.courseId),
      attendanceId: String(task.attendanceId || '')
    }
  })
  closeSignInNotifyModal()
}
</script>

<template>
  <a-modal
    :visible="signInNotifyState.visible"
    :title="!sysStore.isSmallScreen ? t('task.signIn.reminder') : ''"
    :footer="false"
    :mask-closable="false"
    :style="{
      zIndex: signInNotifyModalZIndex
    }"
    :width="sysStore.isSmallScreen ? '100%' : '440px'"
    :class="sysStore.isSmallScreen ? 'sign-in-notify-modal-mobile' : 'sign-in-notify-modal'"
    :modal-style="sysStore.isSmallScreen ? { background: 'transparent', boxShadow: 'none', overflow: 'visible' } : {}"
    :body-style="sysStore.isSmallScreen ? { padding: 0, background: 'transparent', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'visible' } : {}"
    @cancel="goToNextSignInNotifyTask"
  >
    <div
      v-if="sysStore.isSmallScreen"
      class="sign-in-notify-mobile"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @touchcancel="handleTouchEnd"
    >
      <div class="sign-in-notify-mobile__viewport">
        <div class="sign-in-notify-mobile__window">
          <div class="sign-in-notify-mobile__track" :class="{ 'sign-in-notify-mobile__track--dragging': isDragging }" :style="mobileTrackStyle">
            <div
              v-for="(item, index) in taskList"
              :key="item.taskId"
              class="sign-in-notify-mobile__card"
              :class="{
                'sign-in-notify-mobile__card--active': index === signInNotifyState.currentTaskIndex,
                'sign-in-notify-mobile__card--has-prev': index === signInNotifyState.currentTaskIndex && hasPrevTask,
                'sign-in-notify-mobile__card--has-next': index === signInNotifyState.currentTaskIndex && hasNextTask
              }"
              @click="setCurrentSignInNotifyTask(index)"
            >
              <h3 class="sign-in-notify-mobile__title mt-0">
                {{ t('task.signIn.reminder') }}
              </h3>
              <p class="sign-in-notify-mobile__course">
                {{ getTitleByTask(item) }}
              </p>
              <p class="sign-in-notify-mobile__date">
                {{ getWeekTextByTask(item) }}&nbsp;&nbsp;{{ getWeekdayTextByTask(item) }}
              </p>
              <p class="sign-in-notify-mobile__status">
                {{ getStatusTextByTask(item) }}
              </p>
              <div
                v-if="hasCountdown && index === signInNotifyState.currentTaskIndex && !isTaskCountdownEnded(item)"
                class="sign-in-notify-mobile__countdown"
              >
                <span class="sign-in-notify-mobile__countdown-label">{{ t('task.signIn.countdownLabel') }}</span>
                <span class="sign-in-notify-mobile__time-box">{{ countdown.h }}</span>
                <span class="sign-in-notify-mobile__time-sep">:</span>
                <span class="sign-in-notify-mobile__time-box">{{ countdown.m }}</span>
                <span class="sign-in-notify-mobile__time-sep">:</span>
                <span class="sign-in-notify-mobile__time-box">{{ countdown.s }}</span>
              </div>
              <a-button
                type="primary"
                size="large"
                class="sign-in-notify-mobile__confirm"
                :disabled="isTaskCountdownEnded(item)"
                @click.stop="handleGoSignIn"
              >
                {{ t('task.signIn.goToCheckIn') }}
              </a-button>
              <button class="sign-in-notify-mobile__cancel" type="button" @click.stop="goToNextSignInNotifyTask">
                {{ t('task.signIn.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="taskList.length && taskList.length > 1" class="sign-in-notify__dots sign-in-notify__dots--mobile">
        <span
          v-for="(item, index) in taskList"
          :key="item.taskId"
          class="sign-in-notify__dot"
          :class="{ 'sign-in-notify__dot--active': index === signInNotifyState.currentTaskIndex }"
          @click="setCurrentSignInNotifyTask(index)"
        ></span>
      </div>
    </div>
    <div
      v-else
      class="sign-in-notify"
      @touchstart="handleTouchStart"
      @touchend="handleTouchEnd"
    >
      <h3 class="sign-in-notify__title">
        {{ getTitle }}
      </h3>
      <p class="sign-in-notify__date">
        {{ getWeekText }}&nbsp;&nbsp;{{ getWeekdayText }}
      </p>
      <p class="sign-in-notify__status">
        {{ getStatusTextByTask(currentTask) }}
      </p>
      <div v-if="hasCountdown && !isTaskCountdownEnded(currentTask)" class="sign-in-notify__countdown">
        <span class="sign-in-notify__countdown-label">{{ t('task.signIn.countdownLabel') }}</span>
        <span class="sign-in-notify__time-box">{{ countdown.h }}</span>
        <span class="sign-in-notify__time-sep">:</span>
        <span class="sign-in-notify__time-box">{{ countdown.m }}</span>
        <span class="sign-in-notify__time-sep">:</span>
        <span class="sign-in-notify__time-box">{{ countdown.s }}</span>
      </div>
      <div class="sign-in-notify__actions">
        <a-button
          size="large"
          class="sign-in-notify__btn sign-in-notify__btn--cancel"
          @click="goToNextSignInNotifyTask"
        >
          {{ t('task.signIn.cancel') }}
        </a-button>
        <a-button
          type="primary"
          size="large"
          class="sign-in-notify__btn sign-in-notify__btn--confirm"
          :disabled="isTaskCountdownEnded(currentTask)"
          @click="handleGoSignIn"
        >
          {{ t('task.signIn.goToCheckIn') }}
        </a-button>
      </div>
      <div class="sign-in-notify__dots">
        <span
          v-for="(item, index) in taskList"
          :key="item.taskId"
          class="sign-in-notify__dot"
          :class="{ 'sign-in-notify__dot--active': index === signInNotifyState.currentTaskIndex }"
          @click="setCurrentSignInNotifyTask(index)"
        ></span>
      </div>
    </div>
  </a-modal>
</template>

<style lang="less">
.sign-in-notify-modal-mobile {
  .arco-modal-header {
    display: none !important;
  }
  .arco-modal-body {
    background: transparent !important;
    padding: 0 !important;
    overflow: visible !important;
  }
  .arco-modal-content {
    background: transparent !important;
    box-shadow: none !important;
    overflow: visible !important;
  }
}
</style>

<style scoped lang="less">
.sign-in-notify-mobile {
  width: 100vw;
  padding: 0 16px calc(34px + env(safe-area-inset-bottom));
  box-sizing: border-box;
}

.sign-in-notify-mobile__viewport {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  overflow: visible;
}

.sign-in-notify-mobile__window {
  width: min(311px, calc(100vw - 88px));
  overflow: visible;
}

.sign-in-notify-mobile__track {
  --active-index: 0;
  --drag-offset: 0px;
  --card-width: min(311px, calc(100vw - 88px));
  --card-gap: 14px;
  display: flex;
  gap: var(--card-gap);
  transform: translateX(calc((var(--card-width) + var(--card-gap)) * var(--active-index) * -1 + var(--drag-offset)));
  transition: transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
  will-change: transform;
}

.sign-in-notify-mobile__track--dragging {
  transition: none;
}

.sign-in-notify-mobile__card {
  position: relative;
  z-index: 1;
  width: var(--card-width);
  flex: 0 0 var(--card-width);
  border-radius: 32px;
  background: #FFFFFF;
  box-shadow: 0 20px 60px rgba(15, 35, 95, 0.16);
  padding: 44px 32px 26px;
  text-align: center;
  box-sizing: border-box;
  transition: transform 0.26s ease, box-shadow 0.26s ease;
}

.sign-in-notify-mobile__card--active {
  z-index: 2;
  box-shadow: 0 24px 64px rgba(15, 35, 95, 0.2);
}

.sign-in-notify-mobile__card--has-prev {
  transform: translateX(2px);
}

.sign-in-notify-mobile__card--has-next {
  transform: translateX(-2px);
}

.sign-in-notify-mobile__title {
  font-size: 22px;
  line-height: 30px;
  font-weight: 700;
  color: #2F3C69;
  margin-bottom: 28px;
}

.sign-in-notify-mobile__course {
  font-size: 18px;
  line-height: 30px;
  font-weight: 600;
  color: #3C466D;
  margin-bottom: 20px;
}

.sign-in-notify-mobile__date {
  font-size: 17px;
  line-height: 28px;
  font-weight: 600;
  color: #596483;
  margin-bottom: 18px;
}

.sign-in-notify-mobile__status {
  font-size: 15px;
  line-height: 24px;
  color: #FF6B6B;
  margin-bottom: 18px;
}

.sign-in-notify-mobile__countdown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 28px;
}

.sign-in-notify-mobile__countdown-label {
  font-size: 14px;
  line-height: 22px;
  color: #B4BBCB;
  margin-right: 8px;
}

.sign-in-notify-mobile__time-box {
  min-width: 30px;
  height: 32px;
  padding: 0 6px;
  border-radius: 8px;
  background: #EEF2FF;
  color: #6A85FF;
  font-size: 16px;
  line-height: 32px;
  font-weight: 600;
  box-sizing: border-box;
}

.sign-in-notify-mobile__time-sep {
  color: #9FA9C4;
  font-size: 18px;
  line-height: 32px;
  font-weight: 600;
}

.sign-in-notify-mobile__confirm {
  width: 100%;
  height: 52px;
  border-radius: 16px;
  border: none;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 18px;
}

.sign-in-notify-mobile__cancel {
  border: none;
  background: transparent;
  padding: 0;
  color: #C4C7D1;
  font-size: 16px;
  line-height: 24px;
}

.sign-in-notify-modal {
  :deep(.arco-modal-header) {
    padding: 18px 24px 16px;
    border-bottom: 1px solid #F2F3F5;
    text-align: center;
  }

  :deep(.arco-modal-title) {
    font-size: 20px;
    font-weight: 600;
    color: #1D2129;
  }

  :deep(.arco-modal-body) {
    padding: 24px 16px 14px;
  }
}

.sign-in-notify {
  text-align: center;
}

.sign-in-notify__icon-box {
  width: 22px;
  height: 16px;
  border: 2px dashed #D9DDE4;
  border-radius: 2px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sign-in-notify__icon-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #D9DDE4;
}

.sign-in-notify__title {
  font-size: 18px;
  line-height: 28px;
  font-weight: 700;
  color: #1D2B5A;
  margin-bottom: 20px;
}

.sign-in-notify__date {
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  color: #2F3C69;
  margin-bottom: 20px;
}

.sign-in-notify__status {
  font-size: 16px;
  line-height: 24px;
  color: #FF4D4F;
  margin-bottom: 24px;
}

.sign-in-notify__countdown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
}

.sign-in-notify__countdown-label {
  font-size: 14px;
  line-height: 22px;
  color: #93A1BF;
  margin-right: 8px;
}

.sign-in-notify__time-box {
  min-width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #F2F6FF;
  color: #5B7CFF;
  font-size: 24px;
  line-height: 40px;
  font-weight: 600;
  padding: 0 4px;
  box-sizing: border-box;
}

.sign-in-notify__time-sep {
  color: #5B7CFF;
  font-size: 24px;
  line-height: 40px;
  font-weight: 600;
}

.sign-in-notify__actions {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 16px 0 0;
  border-top: 1px solid #F2F3F5;
}

.sign-in-notify__btn {
  width: 132px;
  height: 44px;
  border-radius: 8px;
  font-size: 18px;
}

.sign-in-notify__btn--cancel {
  background: #F5F7FA;
  border-color: #F5F7FA;
  color: #4E5969;
}

.sign-in-notify__btn--confirm {
  background: #4C8DFF;
  border-color: #4C8DFF;
}

.sign-in-notify__dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding-top: 14px;
}

.sign-in-notify__dots--mobile {
  padding-top: 18px;
}

.sign-in-notify__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #E5E6EB;
}

.sign-in-notify__dot--active {
  width: 30px;
  border-radius: 10px;
  background: #4C8DFF;
}
</style>
